import React from 'react';
import styled from 'styled-components';
import { PageContainer } from '../App';
import MetaTags from '../components/MetaTags';

const PrivacyPolicyPage = () => {
  return (
    <>
      <MetaTags
        title="Privacy Policy - Parenting Beyond Perfection"
        description="Our commitment to protecting your privacy and personal information"
        type="website"
      />
      <PageContainer>
        <PolicyContainer>
          <PolicyHeader>Privacy Policy</PolicyHeader>
          <LastUpdated>Last updated: {new Date().toLocaleDateString()}</LastUpdated>

          <Section>
            <SectionTitle>Introduction</SectionTitle>
            <p>
              At Parenting Beyond Perfection ("we," "our," or "us"), we respect your privacy and are committed to protecting your personal information. This Privacy Policy explains how we collect, use, and safeguard your information when you use our website (yourparentingtips.com).
            </p>
          </Section>

          <Section>
            <SectionTitle>Information We Collect</SectionTitle>
            <SubSection>
              <SubTitle>Information You Provide</SubTitle>
              <ul>
                <li>Email address (when subscribing to our newsletter)</li>
                <li>Name and contact information (when using our contact form)</li>
                <li>Authentication information (when signing in with Google)</li>
              </ul>
            </SubSection>

            <SubSection>
              <SubTitle>Automatically Collected Information</SubTitle>
              <ul>
                <li>Device information (browser type, operating system)</li>
                <li>Usage data (pages visited, time spent on site)</li>
                <li>IP address and location data</li>
                <li>Cookies and similar tracking technologies</li>
              </ul>
            </SubSection>
          </Section>

          <Section>
            <SectionTitle>How We Use Your Information</SectionTitle>
            <ul>
              <li>To provide and maintain our website</li>
              <li>To send newsletters and updates (with your consent)</li>
              <li>To respond to your inquiries and contact form submissions</li>
              <li>To improve our website and user experience</li>
              <li>To protect against unauthorized access and abuse</li>
            </ul>
          </Section>

          <Section>
            <SectionTitle>Data Storage and Security</SectionTitle>
            <p>
              We use industry-standard security measures to protect your data, including AWS Amplify and Amazon Cognito for authentication. Your data is stored securely in multiple AWS regions for enhanced reliability and performance:
            </p>
            <SubSection>
              <SubTitle>Data Storage Regions</SubTitle>
              <ul>
                <li>Primary Region: US East (N. Virginia)</li>
                <li>Secondary Region: US West (Oregon)</li>
              </ul>
            </SubSection>
            <SubSection>
              <SubTitle>Security Measures</SubTitle>
              <ul>
                <li>Encryption at rest and in transit across all regions</li>
                <li>Multi-region data replication for high availability</li>
                <li>Automated backup and disaster recovery procedures</li>
                <li>Regular security assessments and monitoring</li>
                <li>Strict access controls and authentication</li>
              </ul>
            </SubSection>
            <p>
              Our multi-region infrastructure ensures your data remains available and protected even in the event of regional service disruptions.
            </p>
          </Section>

          <Section>
            <SectionTitle>Third-Party Services</SectionTitle>
            <p>
              We use trusted third-party services for specific functions:
            </p>
            <ul>
              <li>Google Authentication for secure sign-in</li>
              <li>AWS services for hosting and data storage</li>
              <li>Analytics tools to improve our service</li>
            </ul>
          </Section>

          <Section>
            <SectionTitle>Your Rights</SectionTitle>
            <p>
              You have the right to:
            </p>
            <ul>
              <li>Access your personal information</li>
              <li>Correct inaccurate data</li>
              <li>Request deletion of your data</li>
              <li>Withdraw consent for newsletter subscriptions</li>
              <li>Request a copy of your data</li>
              <li>Know where your data is stored and processed</li>
            </ul>
          </Section>

          <Section>
            <SectionTitle>Children's Privacy</SectionTitle>
            <p>
              Our website is not intended for children under 13. We do not knowingly collect personal information from children under 13. If you believe we have collected information from a child under 13, please contact us immediately.
            </p>
          </Section>

          <Section>
            <SectionTitle>Contact Us</SectionTitle>
            <p>
              If you have questions about this Privacy Policy, our data handling practices, or our multi-region infrastructure, please contact us through our contact form:
            </p>
            <ContactInfo>
              <a href="/contact">Contact Form</a>
            </ContactInfo>
          </Section>
        </PolicyContainer>
      </PageContainer>
    </>
  );
};

const PolicyContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem 0;
`;

const PolicyHeader = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: #292929;
  margin: 0 0 1rem;
  font-family: 'Source Serif 4', Georgia, serif;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const LastUpdated = styled.p`
  color: #757575;
  font-size: 0.9rem;
  margin-bottom: 2rem;
`;

const Section = styled.section`
  margin: 2rem 0;

  ul {
    padding-left: 1.5rem;
    margin: 1rem 0;
    
    li {
      margin: 0.5rem 0;
      color: #292929;
      line-height: 1.6;
    }
  }

  p {
    color: #292929;
    line-height: 1.6;
    margin: 1rem 0;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #292929;
  margin: 1.5rem 0 1rem;
  font-family: 'Source Serif 4', Georgia, serif;
`;

const SubSection = styled.div`
  margin: 1.5rem 0;
`;

const SubTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 600;
  color: #292929;
  margin: 1rem 0;
`;

const ContactInfo = styled.div`
  background: #f8f9fa;
  padding: 1.5rem;
  border-radius: 8px;
  margin-top: 1rem;

  a {
    color: #1a8917;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

export default PrivacyPolicyPage; 